import React, { useState } from 'react';
import { PasswordAuthForm } from './PasswordAuthForm';
import { authService } from '../services/authService';
import { getGlobalBootstrap } from '../utils/appBootstrap';

interface SmartAuthWrapperProps {
  onSuccess: (user: any, token: string) => void;
  onError: (error: string) => void;
}

export const SmartAuthWrapper: React.FC<SmartAuthWrapperProps> = ({
  onSuccess,
  onError
}) => {
  // Get bootstrap data instead of doing environment detection
  const bootstrap = getGlobalBootstrap();
  const [isLoading, setIsLoading] = useState(false);

  const handleTelegramLogin = async () => {
    try {
      setIsLoading(true);
      
      if (window.Telegram && window.Telegram.WebApp) {
        const webapp = window.Telegram.WebApp;
        const initData = webapp.initData;
        
        if (initData) {
          const result = await authService.login(initData);
          onSuccess(result.user, result.token);
        } else {
          onError('Telegram data not available');
        }
      } else {
        onError('Telegram WebApp not available');
      }
    } catch (error) {
      console.error('Telegram login failed:', error);
      onError('Telegram login failed');
    } finally {
      setIsLoading(false);
    }
  };

  // If bootstrap failed, show fallback password auth
  if (!bootstrap) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <PasswordAuthForm
            mode="login"
            onSuccess={onSuccess}
            onError={onError}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Authentication Forms - Based on Bootstrap Detection */}
        {bootstrap.environment === 'telegram' ? (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-center mb-6 text-gray-800">
              Telegram Authentication
            </h2>
            
            <div className="space-y-4">
              <div className="text-center text-gray-600 mb-4">
                <p>Click the button below to authenticate with your Telegram account</p>
              </div>
              
              <button
                onClick={handleTelegramLogin}
                disabled={isLoading}
                className={`w-full py-3 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  isLoading 
                    ? 'bg-gray-400 text-gray-700 cursor-not-allowed' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {isLoading ? '🔄 Authenticating...' : '🚀 Sign in with Telegram'}
              </button>
            </div>
          </div>
        ) : (
          <PasswordAuthForm
            mode="login"
            onSuccess={onSuccess}
            onError={onError}
          />
        )}
        
        {/* Environment Info (Development Only) */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mt-4 p-3 bg-gray-100 rounded-md text-xs text-gray-600">
            <p><strong>Debug Info (Bootstrap-based):</strong></p>
            <p>Environment: {bootstrap.environment}</p>
            <p>Auth Required: {bootstrap.authRequired ? 'Yes' : 'No'}</p>
            <p>Is Authenticated: {bootstrap.isAuthenticated ? 'Yes' : 'No'}</p>
            <p>User Status: {bootstrap.userStatus}</p>
            <p>Target Route: {bootstrap.targetRoute}</p>
            <p>Has Valid Token: {bootstrap.hasValidToken ? 'Yes' : 'No'}</p>
          </div>
        )}
      </div>
    </div>
  );
};